/*
 * Copyright 2026 NXGN Management, LLC. All Rights Reserved.
 */

import { Component, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { ToastrService } from "ngx-toastr";
import { MerchantApplicationRequest } from "../../../models/merchant-application";
import { NotificationService, OnboardingService } from "../../../services";
import { requiredTrimmed } from "../../../validators/MPCustomValidator";

@Component({
  selector: "app-initiate-application",
  templateUrl: "./initiate-application.component.html"
})
export class InitiateApplicationComponent implements OnInit {
  applicationForm!: FormGroup;
  pricingProfiles: string[] = [];
  softwareNames: string[] = [];
  processorTypes: string[] = ["Global Payments"];
  faPlus = faPlus;
  faTrash = faTrash;
  actionInProgress = false;
  profilesLoading = false;

  constructor(
    private onboardingService: OnboardingService,
    private notificationService: NotificationService,
    private toast: ToastrService,
    private router: Router,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadProfiles();
  }

  get form() {
    return this.applicationForm.controls;
  }

  readonly maxLocations = 15;

  get canAddLocation(): boolean {
    return this.locations.length < this.maxLocations;
  }

  get locations(): FormArray {
    return this.applicationForm.get("locations") as FormArray;
  }

  addLocation(): void {
    if (!this.canAddLocation) {
      return;
    }
    this.locations.push(this.createLocation());
  }

  removeLocation(index: number): void {
    if (this.locations.length > 1) {
      this.locations.removeAt(index);
    }
  }

  submit(): void {
    if (this.applicationForm.invalid || this.actionInProgress) {
      this.applicationForm.markAllAsTouched();
      return;
    }
    this.actionInProgress = true;
    this.onboardingService.submitMerchantApplication(this.buildRequest()).subscribe(
      () => {
        this.actionInProgress = false;
        this.toast.success("Application submitted successfully");
        this.buildForm();
      },
      (error) => {
        this.actionInProgress = false;
        this.notificationService.error(error.error);
      }
    );
  }

  cancel(): void {
    this.router.navigate(["/merchants"]);
  }

  private loadProfiles(): void {
    this.profilesLoading = true;
    this.onboardingService.getProfiles().subscribe(
      (profiles) => {
        this.profilesLoading = false;
        this.pricingProfiles = profiles.pricing_profiles || [];
        this.softwareNames = profiles.software_names || [];
        this.form.pricingProfile.setValue(this.onlyOption(this.pricingProfiles));
        this.form.softwareName.setValue(this.onlyOption(this.softwareNames));
      },
      (error) => {
        this.profilesLoading = false;
        this.notificationService.error(error.error);
      }
    );
  }

  private onlyOption(options: string[]): string {
    return options.length === 1 ? options[0] : "";
  }

  private buildRequest(): MerchantApplicationRequest {
    const value = this.applicationForm.value;
    return {
      legal_name: value.legalName.trim(),
      pricing_profile: value.pricingProfile,
      software_name: value.softwareName,
      primary_contact: {
        first_name: value.firstName.trim(),
        last_name: value.lastName.trim(),
        email: value.email.trim()
      },
      merchants: value.locations.map((location: { dbaName: string }) => ({
        dba: location.dbaName.trim()
      }))
    };
  }

  private buildForm(): void {
    this.applicationForm = this.fb.group({
      processorType: [this.processorTypes[0], Validators.required],
      pricingProfile: [this.onlyOption(this.pricingProfiles), Validators.required],
      softwareName: [this.onlyOption(this.softwareNames), Validators.required],
      legalName: ["", requiredTrimmed],
      firstName: ["", requiredTrimmed],
      lastName: ["", requiredTrimmed],
      email: ["", [Validators.required, Validators.email]],
      locations: this.fb.array([this.createLocation()], Validators.required)
    });
  }

  private createLocation(): FormGroup {
    return this.fb.group({ dbaName: ["", requiredTrimmed] });
  }
}
